"use client"

import { Button } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import React, { FC, useState } from "react"
import { BusinessCard } from "./BusinessCard"
import { useMutation } from "@apollo/client"
import { InviteToJobMutation } from "../InviteToJobDialog"
import { toast } from "@/components/Toast"
import { BreadCrumbRef, NovaSession } from "@/types"
import { CheckIcon } from "@heroicons/react/24/outline"

const InviteToJobCardFragment = graphql(`
  fragment InviteToJobCard on IndividualCreative {
    __typename
    id
    name
    ...CreativeBusinessCard
  }
`)

interface Props {
  creative: FragmentType<typeof InviteToJobCardFragment> | undefined | null
  jobId: string
  onInvite: () => void
  serverSession: NovaSession
  refetchQueries?: string[]
  breadCrumbRef: BreadCrumbRef
  trackingEntryPoint: string
}

const checkMark = () => <CheckIcon className="size-[20px] stroke-primary-5" />

export const InviteToJobCard: FC<Props> = props => {
  const { jobId, serverSession, onInvite, breadCrumbRef, trackingEntryPoint } = props
  const [invited, setInvited] = useState(false)
  const [inviteToJob] = useMutation(InviteToJobMutation)
  const creative = useFragment(InviteToJobCardFragment, props.creative)

  const handleInvite = async () => {
    setInvited(true)
    let response

    try {
      response = await inviteToJob({
        variables: {
          input: {
            creative: creative?.id!,
            jobPosting: jobId,
          },
        },
        refetchQueries: props.refetchQueries,
      })
      toast.success(`Invited ${creative?.name} to job`)
      onInvite()
    } catch (error) {
      setInvited(false)
      toast.error("Error inviting creative to job")
    }

    if (response?.data?.inviteToJob?.errors && response?.data?.inviteToJob?.errors.length > 0) {
      setInvited(false)
      toast.error("Error inviting creative to job")
    }
  }

  return (
    <BusinessCard
      creative={creative}
      type="grid"
      trackingEntryPoint={trackingEntryPoint}
      breadCrumbRef={breadCrumbRef}
      serverSession={serverSession}
      onlyShowActionsOnHover={false}
      actionButtons={
        <Button
          onClick={handleInvite}
          variant={invited ? "secondary" : "tertiary"}
          disabled={invited}
          LeftElement={invited ? checkMark : null}>
          {invited ? "Invited" : "Invite to job"}
        </Button>
      }
    />
  )
}
